import { Component, Injector, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { ChassisFormHandler } from '../../form/classes/form-handler';
import { RouteService } from '../../../routing/services/route.service';
import { ButtonInterface, ToolbarService } from '../../../layouts/toolbar/services/toolbar.service';
import { LoaderService } from '../../../support/loading/loader.service';
import { AlertService } from '../../../alerts/services/alert.service';
import { ToastService } from '../../../toast/services/toast.service';
import { PluginService } from '../../../plugins/services/plugin.service';
import { Model } from '../../../api/model';
import { trans } from '../../../localization/helpers';
import { ALERT_MESSAGES } from '../../../alerts/alert-messages';
import { button, BUTTONS } from '../../../support/buttons';
import { DatatableQueryService } from '../../../datatables/services/datatable-query.service';

@Component({
    selector: 'chassis-form-layout',
    templateUrl: './form-layout.html',
    styleUrls: ['./form-layout.scss'],
})
export class FormLayoutComponent implements OnInit {

    /**
     * The mode of the page that is being shown
     *
     * @deprecated
     * @type {string}
     */
    @Input() pageMode;

    @Input() mode: 'create' | 'edit' | 'show';

    /**
     * The Singular form of the model that's being shown
     *
     * @type {string}
     */
    @Input() singular: string;

    /**
     * The plural form of the model that's being shown
     *
     * @type {string}
     */
    @Input() plural: string;

    /**
     * The title of the page;
     */
    @Input() title: string;

    /**
     * The route prefix for the buttons
     */
    @Input() routePrefix: string;

    /**
     * The plugin slot prefix.
     *
     * @type {string}
     */
    @Input() pluginPrefix: string;

    /**
     * The plugin slot data.
     *
     * @type {Object}
     */
    @Input() pluginData = {};

    /**
     * Should we allow the user to save the form?.
     *
     * @type {Object}
     */
    @Input() canSave = true;

    /**
     * The ChassisFormHandler form instance.
     *
     * @type {ChassisFormHandler}
     */
    @Input() form: ChassisFormHandler;

    /**
     * Toolbar object to override defaults.
     *
     * @deprecated Use the toolbar service directly
     */
    @Input() toolbar: {
        buttons?: {
            [key: string]: ButtonInterface | boolean
        } | boolean
    };

    /**
     * Disables the toolbar if set to true
     *
     * @type {boolean}
     * @deprecated Use toolbar = false
     */
    @Input() disableToolbar = false;

    /**
     * The RouteService instance.
     */
    protected routeService: RouteService;
    /**
     * The ToolbarService instance.
     */
    protected toolbarService: ToolbarService;
    /**
     * The LoaderService instance.
     */
    protected loaderService: LoaderService;
    /**
     * The ToastService instance.
     */
    protected toastService: ToastService;
    /**
     * The AlertService instance.
     */
    protected alertService: AlertService;
    /**
     * The PluginService instance.
     */
    protected pluginService: PluginService;

    protected activatedRoute: ActivatedRoute;

    protected location: Location;

    protected datatableQueryService: DatatableQueryService;

    public constructor(protected injector: Injector) {
        this.routeService          = this.injector.get(RouteService);
        this.toolbarService        = this.injector.get(ToolbarService);
        this.loaderService         = this.injector.get(LoaderService);
        this.toastService          = this.injector.get(ToastService);
        this.alertService          = this.injector.get(AlertService);
        this.pluginService         = this.injector.get(PluginService);
        this.activatedRoute        = this.injector.get(ActivatedRoute);
        this.location              = this.injector.get(Location);
        this.datatableQueryService = this.injector.get(DatatableQueryService);
    }

    public ngOnInit(): void {
        if (this.pageMode) {
            this.mode = this.pageMode;
            console.warn(`Form layout pageMode property is deprecated, 
            please use mode instead`);
        }

        if (this.isCreatePage()) {
            this.form.afterSuccess((model) => {
                this.navigateToEdit(model);
            });
        }

        this.createToolbar();
    }

    protected createToolbar(): void {
        if (this.toolbar === false || this.disableToolbar) {
            return;
        }

        if (!this.routePrefix) {
            throw new Error('No route prefix provided');
        }

        this.addToolbarButtons();
    }

    protected addToolbarButtons(): void {
        if (this.needsToAddBackButton()) {
            this.addBackButton();
        }

        if (this.needsToAddDeleteButton()) {
            this.addDeleteButton();
        }

        if (this.needsToAddSubmitButton()) {
            this.addToolbarSaveButtons();
        }
    }

    protected addBackButton(): void {
        this.addToolbarButton(
            button(BUTTONS.BACK, {
                callback: () => {
                    this.cancelCallback();
                }
            })
        );
    }

    protected addDeleteButton(): void {
        const loader = this.loaderService.create();

        this.addToolbarButton(
            button(BUTTONS.DELETE, {
                callback: () => {
                    loader.start();

                    this.deleteCallback().then(() => {
                        loader.stop();
                    }, () => {
                        loader.stop();
                    });
                },
                loader: () => {
                    return loader.isLoading();
                }
            })
        );
    }

    protected needsToAddBackButton(): boolean {
        return !this.toolbar || !this.toolbar.buttons || this.toolbar && this.toolbar !== false && this.toolbar.buttons
            && this.toolbar.buttons !== false && (<any>this.toolbar.buttons).cancel !== false;
    }

    protected needsToAddDeleteButton(): boolean {
        if (!this.canSave) {
            return false;
        }

        if (this.mode === 'create') {
            return false;
        }

        return !this.toolbar || !this.toolbar.buttons || this.toolbar && this.toolbar !== false && this.toolbar.buttons
            && this.toolbar.buttons !== false && (<any>this.toolbar.buttons).delete !== false;
    }

    protected addToolbarSaveButtons(): void {
        const loader = this.loaderService.create();

        this.addToolbarButton(
            button(BUTTONS.SAVE, {
                callback: () => {
                    loader.start();

                    this.submitCallback().then(() => {
                        loader.stop();
                    }, () => {
                        loader.stop();
                    });
                },
                loader: () => {
                    return loader.isLoading();
                }
            })
        );

        this.addToolbarButton(
            button(BUTTONS.SAVE_BACK_TO_INDEX, {
                callback: () => {
                    loader.start();

                    this.form.afterSuccess((model) => {
                        loader.stop();

                        this.routeBackToIndex();
                    });

                    this.submitCallback();
                },
                loader: () => {
                    return loader.isLoading();
                }
            })
        );
    }

    protected needsToAddSubmitButton(): boolean {
        if (!this.canSave) {
            return false;
        }

        return !this.toolbar || !this.toolbar.buttons || this.toolbar && this.toolbar !== false && this.toolbar.buttons
            && this.toolbar.buttons !== false && (<any>this.toolbar.buttons).submit !== false;
    }

    protected submitCallback(): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.canSave) {
                reject();
            }

            if (this.toolbar && this.toolbar.buttons && (<any>this.toolbar.buttons).submit && (<any>this.toolbar.buttons).submit.callback) {

                // change loader callback if set
                if ((<any>this.toolbar.buttons).submit.loader) {
                    this.toolbarService.get(BUTTONS.SAVE.NAME).update({
                        loader: (<any>this.toolbar.buttons).submit.loader
                    });
                }

                (<any>this.toolbar.buttons).submit.callback(resolve, reject);

                return;
            }

            this.form.success((model) => {
                resolve();

                return Promise.resolve();
            });

            this.form.error(() => {
                reject();
            });

            this.form.submit();

        });
    }

    protected addToolbarButton(options: ButtonInterface): void {
        this.toolbarService.add(options);
    }

    private cancelCallback(): void {
        const resolve = () => {
        };

        const reject = () => {
        };

        if (this.toolbar && this.toolbar.buttons && this.toolbar.buttons['cancel'] && (<any>this.toolbar.buttons['cancel']).callback) {
            (<any>this.toolbar.buttons['cancel']).callback(resolve, reject);

            return;
        }

        // We'll continue to execute the default action.
        // this.location.back();
        this.routeBackToIndex();

        resolve();
    }

    private deleteCallback(): Promise<any> {
        return new Promise((resolve, reject) => {
            // show alert first
            const alert = this.alertService.open({
                title: trans(ALERT_MESSAGES.ARE_YOU_SURE),
            });

            alert.closing((res: Function, rej: Function) => {
                if (this.toolbar && this.toolbar.buttons && this.toolbar.buttons['delete']
                    && (<any>this.toolbar.buttons['delete']).callback) {

                    (<any>this.toolbar.buttons['delete']).callback();

                    return res();
                }

                this.deleteFormEntityCallback(res, rej);
            });

            alert.closed(() => {
                resolve();

                this.toastService.success('deleted');

                this.routeBackToIndex();
            });

            alert.dismissed(() => {
                reject();
            });
        });
    }

    private deleteFormEntityCallback(resolve: Function, reject: Function): void {
        if (this.form.model instanceof Model) {
            (<Model>this.form.model).delete().subscribe((data) => {
                resolve();
            }, (error) => {
                reject();
            });
        }
    }

    /**
     * Indicates if the current page is a create page.
     */
    public isCreatePage(): boolean {
        return this.mode === 'create';
    }

    private routeBackToIndex(): void {
        const route = this.routeService.route(this.routePrefix + '.index');

        this.routeService.getRouter().navigate([route], {queryParams: this.datatableQueryService.getLastQueryParams()});
    }

    private navigateToEdit(model: any): void {
        this.routeService.navigate(this.routePrefix + '.edit', [
            model.id
        ]);
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/components/form-layout/form-layout.ts